import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert, Button, Card, Dialog, DialogActions, DialogContent, DialogTitle, MenuItem, Select,
  Stack, Table, TableBody, TableCell, TableRow, Typography,
} from '@mui/material';
import { EditOutlined as EditIcon } from '@mui/icons-material';
import AccessService, { PersonAccess as PersonAccessView, describeAccessError } from '../../api/services/access.service';
import type { AccessArea, AccessCell } from '../../access/areas';
import { atLeast, defaultAccess } from '../../access/areas';
import {
  AREA_GROUPS, LEVEL_COLOR, ROLE_LABEL, cellOptions, cellValue, describeCell, sameCell,
} from '../../access/labels';
import { useAuthStore } from '../../store/authStore';
import { Eyebrow } from '../payroll/payrollUI';

// One person's exceptions to their role's access (backend ACCESS_MATRIX.md:
// whole areas, up or down). Shown to whoever holds Access settings, on
// anyone they may govern — never the owner, never yourself.

const SAME = '__same__';

const PersonAccess: React.FC<{ accountId: string; role: string; isSelf: boolean }> = ({ accountId, role, isSelf }) => {
  const { canArea, access: mine, account } = useAuthStore();
  const allowed = canArea('access', 'manage') && role !== 'AO' && role !== 'SA' && !isSelf;

  const [view, setView] = useState<PersonAccessView | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState<Partial<Record<AccessArea, AccessCell>>>({});
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    if (!allowed) return;
    try {
      setView(await AccessService.person(accountId));
      setError(null);
    } catch (err) {
      setError(describeAccessError(err, 'Access could not be loaded.'));
    }
  }, [accountId, allowed]);
  useEffect(() => { load(); }, [load]);

  if (!allowed) return null;

  const scopedOf = (key: AccessArea) => !!view?.areas.find((a) => a.key === key)?.scoped;
  const labelOf = (key: AccessArea) => view?.areas.find((a) => a.key === key)?.label || key;
  const yours = mine || defaultAccess(account?.role);

  const edit = () => {
    if (!view) return;
    setDraft(Object.fromEntries(view.exceptions.map((e) => [e.area, { level: e.level, scope: e.scope }])));
    setError(null);
    setOpen(true);
  };

  const save = async () => {
    setBusy(true);
    setError(null);
    try {
      const exceptions = Object.entries(draft)
        .filter(([, cell]) => cell)
        .map(([area, cell]) => ({ area: area as AccessArea, ...(cell as AccessCell) }));
      setView(await AccessService.savePerson(accountId, exceptions));
      setOpen(false);
    } catch (err) {
      setError(describeAccessError(err, 'That could not be saved.'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Card sx={{ p: 2.5 }}>
      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
        <Eyebrow>Access</Eyebrow>
        {view?.editable && (
          <Button size="small" startIcon={<EditIcon sx={{ fontSize: 16 }} />} onClick={edit} sx={{ color: 'text.secondary', py: 0 }}>
            Edit
          </Button>
        )}
      </Stack>
      {error && !open && <Alert severity="error" sx={{ mb: 1 }}>{error}</Alert>}
      {view && (view.exceptions.length === 0 ? (
        <Typography variant="body2" color="text.secondary">
          Same as every {ROLE_LABEL[role] || role}. Settings → Access sets what that is.
        </Typography>
      ) : (
        <Table size="small" sx={{ '& td': { borderBottom: '1px dashed', borderColor: 'divider', px: 0 } }}>
          <TableBody>
            {view.exceptions.map((e) => (
              <TableRow key={e.area}>
                <TableCell><Typography variant="body2" color="text.secondary">{labelOf(e.area)}</Typography></TableCell>
                <TableCell align="right">
                  <Typography variant="body2" sx={{ fontWeight: 700, color: LEVEL_COLOR[e.level] }}>{describeCell(e, scopedOf(e.area))}</Typography>
                  <Typography variant="caption" color="text.secondary">
                    {ROLE_LABEL[role] || role}s: {describeCell(view.fromRole[e.area], scopedOf(e.area))}
                  </Typography>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      ))}

      <Dialog open={open} onClose={busy ? undefined : () => setOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Access for {view?.name}</DialogTitle>
        <DialogContent>
          {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
          {view && (
            <Table size="small">
              <TableBody>
                {AREA_GROUPS.flatMap((g) => g.areas).map(({ key }) => {
                  // The access row is the owner's to set, and only for CAs.
                  if (key === 'access' && (account?.role !== 'AO' || role !== 'CA')) return null;
                  const scoped = scopedOf(key);
                  const own = draft[key];
                  const fromRole = view.fromRole[key];
                  return (
                    <TableRow key={key}>
                      <TableCell sx={{ pl: 0 }}><Typography variant="body2">{labelOf(key)}</Typography></TableCell>
                      <TableCell align="right" sx={{ pr: 0 }}>
                        <Select
                          size="small"
                          variant="standard"
                          disableUnderline
                          value={own ? cellValue(own, scoped) : SAME}
                          onChange={(e) => {
                            const value = e.target.value as string;
                            if (value === SAME) {
                              setDraft((d) => { const n = { ...d }; delete n[key]; return n; });
                              return;
                            }
                            const opt = cellOptions(scoped).find((o) => o.value === value);
                            if (opt) setDraft((d) => ({ ...d, [key]: opt.cell }));
                          }}
                          renderValue={(value) => (value === SAME
                            ? <Typography variant="body2" color="text.secondary">Same as role · {describeCell(fromRole, scoped)}</Typography>
                            : <Typography variant="body2" sx={{ fontWeight: 700, color: LEVEL_COLOR[own!.level] }}>{describeCell(own, scoped)}</Typography>)}
                          inputProps={{ 'aria-label': `${labelOf(key)} for ${view.name}` }}
                          // Sized to its value, so the arrow sits next to the words.
                          sx={{ '& .MuiSelect-select': { pr: '24px !important' } }}
                        >
                          <MenuItem value={SAME}>
                            <Typography variant="body2" color="text.secondary">Same as role · {describeCell(fromRole, scoped)}</Typography>
                          </MenuItem>
                          {cellOptions(scoped)
                            .filter((opt) => !sameCell(opt.cell, fromRole, scoped))
                            .map((opt) => {
                              const over = !atLeast(yours[key]?.level, opt.cell.level)
                                || (scoped && opt.cell.scope === 'company' && yours[key]?.scope === 'area' && opt.cell.level !== 'none');
                              return (
                                <MenuItem key={opt.value} value={opt.value} disabled={over}>
                                  <Typography variant="body2" sx={{ color: LEVEL_COLOR[opt.cell.level] }}>{opt.label}</Typography>
                                </MenuItem>
                              );
                            })}
                        </Select>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setOpen(false)} disabled={busy}>Cancel</Button>
          <Button variant="contained" disableElevation onClick={save} disabled={busy}>Save</Button>
        </DialogActions>
      </Dialog>
    </Card>
  );
};

export default PersonAccess;
